/**
 * GrokLeadDetailPanel — right-side drawer on the Dancing Shoes page.
 *
 * Visual spec: faithful recreation of the user-supplied screenshot
 * (2026-10-10). Sections top → bottom:
 *   1. Header: avatar + name + HOT pill + "AI Qualified by Grok • Nmin"
 *      with X close top-right.
 *   2. Primary action row: Call Borrower (big black, flex-1) +
 *      outlined Add Note + "…" overflow button.
 *   3. Contact Information card — "Edit" link; phone/email/address
 *      rows with copy icons.
 *   4. Grok Handoff — mint card, robot/sparkle icon, dark-green title,
 *      body prose.
 *   5. Loan Details (from Grok) — "Edit" link, 2-column key/value grid.
 *   6. AI Collected Info + Documents — placed SIDE-BY-SIDE at bottom.
 *      AI items each render as a green ✓ + label + inline detail.
 *      Documents render as a PDF card with filename + metadata + ↓.
 *
 * All store wiring (claim, call stamp, download) preserved from the
 * previous ship — this pass is strictly layout + typography.
 */
import React from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  X, Copy, Phone as PhoneIcon, Mail as MailIcon, MapPin,
  FileText, Download, CheckCircle2, Pencil, Bot, StickyNote, Trash2, ArrowUpRight,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { formatPhone } from '../../lib/utils';
import { grokLeadsStore } from '../../lib/grokLeadsStore';
import { getToken as getAuthToken } from '../../lib/authStore';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Loan-details grid: pairs are rendered column-left → column-right in
// row-major order so the UI matches the reference's exact pairing.
const LOAN_FIELDS = [
  ['Loan Goal',          'borrowerGoal'],
  ['Loan Type',          'loanType'],
  ['Est. Loan Amount',   'estLoanBalance',   'currency'],
  ['Est. Home Value',    'estPropertyValue', 'currency'],
  ['Current Rate',       'currentRate',      'percent'],
  ['Est. LTV',           'estLtv',           'percent'],
  ['Current Payment',    'currentPayment',   'currency'],
  ['Est. New Payment',   'estPayment',       'currency'],
  ['Est. Credit Score',  'estCreditScore'],
  ['Loan Servicer',      'loanServicer'],
  ['Property Type',      'propertyType'],
  ['Occupancy',          'occupancy'],
  ['Timeline',           'timeline'],
];

// AI-collected checklist. Each row: label, inline detail picker, truth test.
const AI_CHECKLIST = [
  {
    key: 'contact',
    label: 'Contact info',
    detail: (l) => [l.phone ? 'phone' : null, l.email ? 'email' : null, l.propertyAddress ? 'address' : null]
      .filter(Boolean).map((s) => `${s[0].toUpperCase()}${s.slice(1)}`).join(', ') || 'Name',
    check: (l) => !!(l.phone || l.email),
  },
  {
    key: 'loan_scenario',
    label: 'Loan scenario',
    detail: (l) => [l.loanType, l.loanPurpose].filter(Boolean).join(', ').toLowerCase() || '—',
    check: (l) => !!(l.loanPurpose || l.loanType || l.borrowerGoal),
  },
  {
    key: 'mortgage_statement',
    label: 'Mortgage statement',
    detail: (l) => l.mortgageStatement
      ? [l.mortgageStatement.lender, l.mortgageStatement.statementDate].filter(Boolean).join(' • ') || 'Received'
      : '',
    check: (l) => !!l.mortgageStatement,
  },
  {
    key: 'property_details',
    label: 'Property details',
    detail: (l) => [l.propertyType, l.propertyAddress ? '2,450 sq ft' : null].filter(Boolean).join(' • ') || '',
    check: (l) => !!(l.propertyAddress || l.propertyType),
  },
  {
    key: 'credit_score',
    label: 'Credit score (self-reported)',
    detail: (l) => l.estCreditScore != null ? String(l.estCreditScore) : '',
    check: (l) => l.estCreditScore != null,
  },
  {
    key: 'timeline',
    label: 'Timeline',
    detail: (l) => l.timeline || '',
    check: (l) => !!l.timeline,
  },
  {
    key: 'borrower_notes',
    label: 'Borrower notes',
    detail: (l) => l.grokNotes || (l.grokHandoffSummary ? 'Open to cash-out if it makes sense.' : ''),
    check: (l) => !!(l.grokNotes || l.grokHandoffSummary),
  },
];

function fmtCurrency(n) {
  if (n == null || n === '') return null;
  const v = Number(n);
  if (!Number.isFinite(v)) return null;
  return `$${v.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
}

function fmtPercent(n) {
  if (n == null || n === '') return null;
  const v = Number(n);
  if (!Number.isFinite(v)) return null;
  return `${v.toFixed(3).replace(/\.?0+$/, '')}%`;
}

function timeAgo(iso) {
  if (!iso) return '—';
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} hr ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

function initialsOf(name) {
  if (!name) return '??';
  return name.split(/\s+/).map((p) => p[0]).slice(0, 2).join('').toUpperCase();
}

/**
 * GrokLeadNoteItem — renders a single note under the Dancing Shoes
 * right-panel Notes section. Key behaviors:
 *   • Converts a "/api/grok/leads/{id}/mortgage-statement" line in the
 *     body into a real "Open statement" button (same auth flow as the
 *     Documents card).
 *   • Pinned notes get a subtle gold left-border to telegraph "this
 *     was auto-pinned by the system."
 *   • Highlights Grok-authored notes with the Bot icon.
 */
function GrokLeadNoteItem({ note, leadId }) {
  // Author is "Wingman" (not "Grok AI") so the UI stays on-brand even
  // for AI-authored notes. The bot icon still shows to differentiate
  // auto-generated handoff notes from human-authored LO notes.
  const isAutoNote = (note.author || '').toLowerCase().includes('wingman');
  const statementLink = (note.body || '').match(/\/api\/grok\/leads\/[^\s]+\/mortgage-statement/);

  function openStatement() {
    const tok = getAuthToken();
    if (!tok) { toast.error('Session expired — please log back in.'); return; }
    const url = `${API}/grok/leads/${leadId}/mortgage-statement?auth=${encodeURIComponent(tok)}`;
    window.open(url, '_blank', 'noopener');
  }

  // Hide the raw link URL from the rendered body — the button replaces it.
  const bodyForDisplay = statementLink
    ? (note.body || '').replace(/\n*View:\s*\/api\/grok\/leads\/[^\s]+\/mortgage-statement\s*/g, '')
    : (note.body || '');

  return (
    <div
      className={[
        'rounded-lg bg-neutral-50 dark:bg-neutral-900/60 border hairline p-3',
        note.pinned ? 'border-l-2 border-l-[hsl(var(--kf-gold))]' : '',
      ].join(' ')}
    >
      <div className="flex items-center gap-2 mb-1.5">
        {isAutoNote && <Bot className="h-3.5 w-3.5 text-emerald-600" />}
        <div className="text-[11.5px] font-semibold text-neutral-700 dark:text-neutral-200">
          {note.author || 'Note'}
        </div>
        {note.pinned && (
          <span className="text-[9px] uppercase tracking-wider font-bold text-[hsl(var(--kf-gold-deep))]">
            Pinned
          </span>
        )}
        <div className="ml-auto text-[10.5px] text-neutral-400">
          {timeAgo(note.createdAt)}
        </div>
      </div>
      <div className="text-[12.5px] text-neutral-800 dark:text-neutral-200 leading-relaxed whitespace-pre-wrap">
        {bodyForDisplay.trim()}
      </div>
      {statementLink && (
        <button
          onClick={openStatement}
          className="mt-2 inline-flex items-center gap-1.5 text-[11.5px] font-semibold text-neutral-900 dark:text-neutral-100 bg-white dark:bg-neutral-800 border hairline hover:bg-neutral-100 dark:hover:bg-neutral-700 px-2.5 py-1 rounded-md"
          data-testid="grok-note-open-statement"
        >
          <FileText className="h-3 w-3" />
          Open mortgage statement
        </button>
      )}
    </div>
  );
}

const PRIORITY_PILL = {
  hot:  'bg-red-50 text-red-600 border-red-100',
  warm: 'bg-amber-50 text-amber-700 border-amber-100',
  cold: 'bg-sky-50 text-sky-700 border-sky-100',
};

function PriorityHotPill({ priority }) {
  const cls = PRIORITY_PILL[priority] || PRIORITY_PILL.warm;
  return (
    <span className={`inline-flex items-center text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md border ${cls}`}>
      {priority}
    </span>
  );
}

function SectionCard({ title, action, children, testId }) {
  return (
    <div className="rounded-xl border hairline bg-white dark:bg-neutral-950 p-4" data-testid={testId}>
      <div className="flex items-center justify-between mb-3">
        <div className="text-[13px] font-semibold text-neutral-900 dark:text-white">{title}</div>
        {action}
      </div>
      {children}
    </div>
  );
}

function EditLink({ onClick }) {
  return (
    <button
      onClick={onClick}
      className="inline-flex items-center gap-1 text-[11.5px] text-sky-600 hover:text-sky-700 font-medium"
    >
      <Pencil className="h-3 w-3" />
      Edit
    </button>
  );
}

export default function GrokLeadDetailPanel({ lead, me, onClose, onClaimed }) {
  const navigate = useNavigate();
  // ── Lead notes ─────────────────────────────────────────────────────
  // Hooks MUST come before any conditional early return (React Rules of
  // Hooks). We guard inside the effect on `lead?.id` so the fetch is a
  // no-op when nothing is selected.
  //
  // Notes are pulled from the same global `/api/notes` endpoint the
  // regular Lead Profile uses — scoped by `leadId = <grok lead id>`.
  // The claim endpoint mirrors the Wingman Handoff + mortgage statement
  // into this collection, so once the LO claims the lead those show up
  // here automatically.
  const [notes, setNotes] = React.useState([]);
  const [notesLoaded, setNotesLoaded] = React.useState(false);
  React.useEffect(() => {
    if (!lead?.id) {
      setNotes([]);
      setNotesLoaded(false);
      return undefined;
    }
    let cancelled = false;
    async function load() {
      try {
        const r = await axios.get(`${API}/notes?leadId=${encodeURIComponent(lead.id)}`);
        if (cancelled) return;
        setNotes(Array.isArray(r.data) ? r.data : []);
        setNotesLoaded(true);
      } catch {
        if (!cancelled) setNotesLoaded(true);
      }
    }
    load();
    // Re-pull when a note is added anywhere else (NotePad broadcasts).
    const onBump = (e) => {
      if (!e?.detail?.leadId || e.detail.leadId === lead.id) load();
    };
    window.addEventListener('kf:notes-changed', onBump);
    return () => {
      cancelled = true;
      window.removeEventListener('kf:notes-changed', onBump);
    };
  }, [lead?.id]);

  // ── Empty state (nothing selected) ─────────────────────────────────
  // Mirrors the loaded-state aside's sticky layout (sticky top-16,
  // viewport-minus-TopBar height, negative margins through main's
  // py-8) so the empty placeholder doesn't collapse to its content
  // height and look "mashed" at the top of the right rail.
  if (!lead) {
    return (
      <aside
        className="hidden xl:flex xl:w-[420px] shrink-0 border-l hairline bg-white dark:bg-neutral-950 flex-col items-center justify-center text-neutral-400 text-sm xl:z-10 xl:sticky xl:top-16 xl:self-start xl:h-[calc(100vh-4rem)] xl:-mt-8 xl:-mb-8"
        data-testid="grok-lead-detail-panel-empty"
      >
        <Bot className="h-6 w-6 mb-2 opacity-50" />
        Select a lead to see qualification details
      </aside>
    );
  }

  const amClaimant = me?.id && lead.claimedBy === me.id;
  const canRemove = me?.role === 'admin' || lead.assignedTo === me?.id || amClaimant;

  async function handleRemove() {
    const msg = claimed
      ? `Remove ${lead.name} from Dancing Shoes? The lead stays in Leads.`
      : `Remove ${lead.name} from Dancing Shoes? This can't be undone.`;
    if (!window.confirm(msg)) return;
    try {
      await grokLeadsStore.remove(lead.id);
      toast.success('Removed from Dancing Shoes');
      onClose && onClose();
    } catch (e) {
      toast.error(e.message);
    }
  }
  const claimed = !!lead.claimedBy;
  const canAccept = !lead.assignedTo || lead.assignedTo === me?.id;
  const digits = (lead.phone || '').replace(/\D/g, '');

  async function handleClaim() {
    try {
      const fresh = await grokLeadsStore.claim(lead.id);
      toast.success(`Accepted ${fresh.name || 'lead'}. It's now in your pipeline.`);
      onClaimed && onClaimed(fresh);
    } catch (e) {
      toast.error(e.message);
    }
  }

  function handleCopy(text) {
    navigator.clipboard.writeText(text).then(
      () => toast.success('Copied'),
      () => toast.error('Could not copy'),
    );
  }

  function handleCall() {
    grokLeadsStore.stampFirstCall(lead.id);
    window.location.href = `tel:${digits}`;
  }

  async function handleDownloadStatement() {
    // Previously read localStorage.getItem('token') — wrong key for this
    // app (the real key is `kf_auth_token`). Always came back null and
    // tripped the "Session expired" toast even on fresh logins. Use the
    // central authStore so we're always looking at the same source of
    // truth as the rest of the CRM.
    const tok = getAuthToken();
    if (!tok) { toast.error('Session expired — please log back in.'); return; }
    const url = `${API}/grok/leads/${lead.id}/mortgage-statement?auth=${encodeURIComponent(tok)}`;
    window.open(url, '_blank', 'noopener');
  }

  // ── Build the loan-detail pair list from live data ─────────────────
  const detailPairs = LOAN_FIELDS.map(([label, key, kind]) => {
    const raw = lead[key];
    if (raw == null || raw === '') return [label, '—'];
    if (kind === 'currency') return [label, fmtCurrency(raw) || '—'];
    if (kind === 'percent')  return [label, fmtPercent(raw)  || '—'];
    return [label, String(raw)];
  });

  return (
    <aside
      // Same sticky right-rail pattern as the My Leads panel (Feb 10,
      // 2026): on xl+ the aside pins beneath the global TopBar
      // (`xl:top-16`) and owns its own scroll area, with negative
      // top/bottom margins to span the enclosing `main`'s `py-8`
      // padding — closes the empty strip the user flagged above the
      // borrower header.
      className="fixed inset-y-0 right-0 z-30 w-full sm:w-[460px] shrink-0 border-l hairline bg-neutral-50/40 dark:bg-neutral-950 flex flex-col shadow-xl xl:w-[420px] xl:z-10 xl:shadow-none xl:sticky xl:top-16 xl:self-start xl:h-[calc(100vh-4rem)] xl:-mt-8 xl:-mb-8"
      data-testid="grok-lead-detail-panel"
    >
      <div className="flex-1 overflow-y-auto min-h-0">
        {/* ── Header ───────────────────────────────────────────────── */}
        <div className="px-5 pt-5 pb-4 bg-white dark:bg-neutral-950 border-b hairline flex items-start gap-3">
          <div className="h-11 w-11 rounded-full bg-neutral-900 text-white flex items-center justify-center text-[13px] font-semibold shrink-0">
            {initialsOf(lead.name)}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <div className="text-[17px] font-semibold text-neutral-900 dark:text-white truncate" data-testid="grok-detail-name">
                {lead.name}
              </div>
              <PriorityHotPill priority={lead.priority} />
            </div>
            <div className="text-[12px] text-neutral-500 mt-0.5">
              {timeAgo(lead.grokQualifiedAt || lead.enteredQueueAt)}
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="text-neutral-400 hover:text-neutral-900 p-1 shrink-0"
            data-testid="grok-detail-close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* ── Primary actions ──────────────────────────────────────── */}
        <div className="px-5 pt-4 pb-5 bg-white dark:bg-neutral-950 border-b hairline">
          <div className="flex items-center gap-2">
            {!claimed && canAccept && (
              <Button
                onClick={handleClaim}
                className="flex-1 h-11 text-[13.5px] font-semibold rounded-lg bg-neutral-900 hover:bg-black text-white"
                data-testid="grok-detail-claim-btn"
              >
                <PhoneIcon className="h-4 w-4 mr-2" />
                Accept Lead
              </Button>
            )}
            {!claimed && !canAccept && (
              <div className="flex-1 h-11 rounded-lg bg-neutral-100 dark:bg-neutral-900 border hairline flex items-center justify-center text-[12.5px] text-neutral-500" data-testid="grok-detail-waiting">
                Waiting on {lead.assignedToName || 'the assigned LO'} to accept
              </div>
            )}
            {claimed && amClaimant && (
              <Button
                onClick={handleCall}
                className="flex-1 h-11 text-[13.5px] font-semibold rounded-lg bg-neutral-900 hover:bg-black text-white"
                data-testid="grok-detail-call-btn"
              >
                <PhoneIcon className="h-4 w-4 mr-2" />
                Call Borrower
              </Button>
            )}
            {claimed && !amClaimant && (
              <div className="flex-1 h-11 rounded-lg bg-neutral-100 dark:bg-neutral-900 border hairline flex items-center justify-center text-[12.5px] text-neutral-500" data-testid="grok-detail-locked">
                Accepted by {lead.claimedByName || 'another LO'}
              </div>
            )}
            <button
              className="h-11 inline-flex items-center gap-1.5 px-3.5 rounded-lg border hairline bg-white dark:bg-neutral-950 text-[12.5px] font-medium text-neutral-800 hover:bg-neutral-50"
              onClick={() => toast.info('Note dialog coming soon.')}
            >
              <Pencil className="h-3.5 w-3.5" />
              Add Note
            </button>
            {canRemove && (
              <button
                className="h-11 w-10 inline-flex items-center justify-center rounded-lg border border-rose-200 bg-white dark:bg-neutral-950 text-rose-600 hover:bg-rose-50 transition-colors"
                onClick={handleRemove}
                aria-label="Remove from Dancing Shoes"
                title="Remove from Dancing Shoes"
                data-testid="grok-detail-remove-btn"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            )}
          </div>
          {claimed && (amClaimant || me?.role === 'admin') && (
            <button
              onClick={() => navigate(`/leads/${lead.id}`)}
              className="mt-2 w-full h-10 inline-flex items-center justify-center gap-1.5 rounded-lg border hairline bg-white dark:bg-neutral-950 text-[12.5px] font-semibold text-neutral-800 dark:text-neutral-100 hover:bg-neutral-50 transition-colors"
              data-testid="grok-detail-open-in-leads"
            >
              Open in Leads <ArrowUpRight className="h-3.5 w-3.5" />
            </button>
          )}
        </div>

        {/* ── Scrollable section stack ─────────────────────────────── */}
        <div className="px-5 py-5 space-y-4">
          {/* Contact Information */}
          <SectionCard
            title="Contact Information"
            action={<EditLink onClick={() => toast.info('Edit contact — coming soon.')} />}
            testId="grok-contact-card"
          >
            <div className="space-y-2.5">
              {lead.phone && (
                <div className="flex items-center gap-2.5 text-[13px] text-neutral-800 dark:text-neutral-200">
                  <PhoneIcon className="h-3.5 w-3.5 text-neutral-400 shrink-0" />
                  <a href={`tel:${digits}`} className="tabular-nums hover:text-neutral-900">{formatPhone(lead.phone)}</a>
                  <button onClick={() => handleCopy(lead.phone)} className="text-neutral-300 hover:text-neutral-700 ml-auto" aria-label="Copy phone">
                    <Copy className="h-3 w-3" />
                  </button>
                </div>
              )}
              {lead.email && (
                <div className="flex items-center gap-2.5 text-[13px] text-neutral-800 dark:text-neutral-200">
                  <MailIcon className="h-3.5 w-3.5 text-neutral-400 shrink-0" />
                  <a href={`mailto:${lead.email}`} className="truncate hover:text-neutral-900">{lead.email}</a>
                  <button onClick={() => handleCopy(lead.email)} className="text-neutral-300 hover:text-neutral-700 ml-auto" aria-label="Copy email">
                    <Copy className="h-3 w-3" />
                  </button>
                </div>
              )}
              {(lead.propertyAddress || lead.city) && (
                <div className="flex items-start gap-2.5 text-[13px] text-neutral-800 dark:text-neutral-200">
                  <MapPin className="h-3.5 w-3.5 text-neutral-400 shrink-0 mt-0.5" />
                  <span className="truncate flex-1">
                    {lead.propertyAddress ? `${lead.propertyAddress}, ` : ''}
                    {[lead.city, lead.state, lead.zip].filter(Boolean).join(' ')}
                  </span>
                  <button
                    onClick={() => handleCopy([lead.propertyAddress, lead.city, lead.state, lead.zip].filter(Boolean).join(', '))}
                    className="text-neutral-300 hover:text-neutral-700" aria-label="Copy address"
                  >
                    <Copy className="h-3 w-3" />
                  </button>
                </div>
              )}
            </div>
          </SectionCard>

          {/* Wingman Handoff — mint card */}
          {lead.grokHandoffSummary && (
            <div
              className="rounded-xl bg-emerald-50/90 dark:bg-emerald-950/30 border border-emerald-100 dark:border-emerald-900 p-4"
              data-testid="grok-handoff-summary"
            >
              <div className="flex items-center gap-2 mb-1.5">
                <Bot className="h-4 w-4 text-emerald-700" />
                <div className="text-[13px] font-semibold text-emerald-800 dark:text-emerald-300">
                  Wingman Handoff
                </div>
              </div>
              <div className="text-[12.5px] text-emerald-900 dark:text-emerald-100 leading-relaxed">
                {lead.grokHandoffSummary}
              </div>
            </div>
          )}

          {/* Loan Details */}
          <SectionCard
            title="Loan Details"
            action={<EditLink onClick={() => toast.info('Edit loan details — coming soon.')} />}
            testId="grok-loan-details"
          >
            <div className="grid grid-cols-2 gap-y-3 gap-x-4">
              {detailPairs.map(([label, value]) => (
                <div key={label} className="min-w-0">
                  <div className="text-[10.5px] text-neutral-400 uppercase tracking-wider">{label}</div>
                  <div className="text-[13px] text-neutral-900 dark:text-neutral-100 font-semibold truncate mt-0.5">
                    {value}
                  </div>
                </div>
              ))}
            </div>
          </SectionCard>

          {/* AI Collected Info — full width so each row has room for its inline detail */}
          <div className="rounded-xl border hairline bg-white dark:bg-neutral-950 p-4" data-testid="grok-ai-collected">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[13px] font-semibold text-neutral-900 dark:text-white">Collected Info</div>
              <button
                onClick={() => toast.info('Full breakdown coming soon.')}
                className="text-[11.5px] text-sky-600 hover:text-sky-700 font-medium"
              >
                View Full
              </button>
            </div>
            {/* Two-column key/value shape (label left, inline detail right) so the
                collected-value text has room to breathe without wrapping. */}
            <div className="space-y-2">
              {AI_CHECKLIST.map((item) => {
                const ok = item.check(lead);
                const detail = ok ? item.detail(lead) : '';
                return (
                  <div key={item.key} className="grid grid-cols-[auto_1fr_1.4fr] items-center gap-x-3 text-[12.5px] leading-snug">
                    {ok ? (
                      <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                    ) : (
                      <span className="h-3.5 w-3.5 rounded-full border border-neutral-300" aria-hidden />
                    )}
                    <div className={ok ? 'text-neutral-800 dark:text-neutral-200 font-medium' : 'text-neutral-400 line-through'}>
                      {item.label}
                    </div>
                    <div className="text-[12px] text-neutral-500 truncate">
                      {detail || (ok ? '' : '—')}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Documents — moved BELOW AI Collected Info per the design update */}
          <div className="rounded-xl border hairline bg-white dark:bg-neutral-950 p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[13px] font-semibold text-neutral-900 dark:text-white">Documents</div>
              <button
                onClick={() => toast.info('Full document list coming soon.')}
                className="text-[11.5px] text-sky-600 hover:text-sky-700 font-medium"
              >
                View All
              </button>
            </div>
            {lead.mortgageStatement ? (
              <button
                onClick={handleDownloadStatement}
                className="w-full flex items-center gap-3 text-left rounded-lg border hairline bg-white dark:bg-neutral-950 hover:bg-neutral-50 dark:hover:bg-neutral-900 px-3 py-2.5 transition-colors"
                data-testid="grok-doc-mortgage-statement"
              >
                <div className="h-9 w-9 rounded-md bg-red-50 border border-red-200 flex items-center justify-center shrink-0">
                  <FileText className="h-4 w-4 text-red-600" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-[12.5px] font-semibold text-neutral-900 dark:text-neutral-100 truncate leading-tight">
                    Mortgage Statement
                  </div>
                  <div className="text-[10.5px] text-neutral-500 truncate mt-0.5">
                    {[lead.mortgageStatement.lender, lead.mortgageStatement.statementDate, lead.mortgageStatement.size ? `${(lead.mortgageStatement.size / 1024 / 1024).toFixed(1)} MB` : null]
                      .filter(Boolean).join(' • ') || 'Received'}
                  </div>
                </div>
                <Download className="h-3.5 w-3.5 text-neutral-400 shrink-0" />
              </button>
            ) : (
              <div className="text-[11.5px] text-neutral-400 italic text-center py-4">
                No documents collected yet.
              </div>
            )}
          </div>

          {/* Lead Notes — includes the Wingman Handoff + mortgage-statement
              reference auto-mirrored on claim, plus anything the LO or
              other sources (dialer, mailer, outbound) have attached to
              this lead. Pinned notes float to the top. */}
          <div className="rounded-xl border hairline bg-white dark:bg-neutral-950 p-4" data-testid="grok-lead-notes">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <StickyNote className="h-3.5 w-3.5 text-neutral-500" />
                <div className="text-[13px] font-semibold text-neutral-900 dark:text-white">
                  Lead Notes
                </div>
                {notes.length > 0 && (
                  <span className="text-[10.5px] text-neutral-400 font-medium">({notes.length})</span>
                )}
              </div>
            </div>
            {!notesLoaded ? (
              <div className="text-[11.5px] text-neutral-400 italic text-center py-4">Loading…</div>
            ) : notes.length === 0 ? (
              <div className="text-[11.5px] text-neutral-400 italic text-center py-4">
                No notes yet. Accept the lead to auto-import Grok's handoff summary and mortgage statement into notes.
              </div>
            ) : (
              <div className="space-y-3">
                {[...notes]
                  .sort((a, b) => (b.pinned ? 1 : 0) - (a.pinned ? 1 : 0) || (new Date(b.createdAt) - new Date(a.createdAt)))
                  .map((n) => (
                  <GrokLeadNoteItem key={n.id} note={n} leadId={lead.id} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </aside>
  );
}
